import { useState, useEffect } from 'preact/hooks';
import { getImportLogs } from '../../lib/api';
import VTImportPopup from './VTImportPopup';

export default function ImportSettings({ onRefresh }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVTModal, setShowVTModal] = useState(false);

  const loadLogs = async () => {
    try {
      const data = await getImportLogs();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Failed to load import logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const handleImportComplete = () => {
    loadLogs();
    if (onRefresh) onRefresh();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseStats = (statsStr) => {
    try {
      return JSON.parse(statsStr);
    } catch {
      return null;
    }
  };

  const styles = {
    container: {
      maxWidth: '800px'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 6px 0'
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      margin: 0
    },
    grid: {
      display: 'grid',
      gap: '16px',
      marginBottom: '32px'
    },
    card: {
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    },
    cardHover: {
      borderColor: '#a78bfa',
      boxShadow: '0 4px 12px rgba(124, 58, 237, 0.1)'
    },
    cardDisabled: {
      background: '#f9fafb',
      cursor: 'default'
    },
    cardContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px'
    },
    iconBox: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    cardBody: {
      flex: 1,
      minWidth: 0
    },
    cardTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 4px 0'
    },
    cardDesc: {
      fontSize: '14px',
      color: '#6b7280',
      margin: '0 0 12px 0',
      lineHeight: '1.5'
    },
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    comingSoonBadge: {
      display: 'inline-block',
      padding: '4px 10px',
      background: '#f3f4f6',
      color: '#6b7280',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500'
    },
    divider: {
      height: '1px',
      background: '#e5e7eb',
      margin: '32px 0 24px 0'
    },
    historyTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 16px 0'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      background: '#f9fafb',
      borderRadius: '12px',
      border: '1px dashed #d1d5db'
    },
    emptyIcon: {
      width: '48px',
      height: '48px',
      background: '#e5e7eb',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 12px auto'
    },
    emptyText: {
      fontSize: '14px',
      color: '#6b7280',
      margin: 0
    },
    emptySubtext: {
      fontSize: '13px',
      color: '#9ca3af',
      margin: '4px 0 0 0'
    },
    logCard: (status) => ({
      background: status === 'failed' ? '#fef2f2' : 'white',
      border: `1px solid ${status === 'failed' ? '#fecaca' : '#e5e7eb'}`,
      borderRadius: '10px',
      padding: '16px',
      marginBottom: '12px'
    }),
    logHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '8px'
    },
    logInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    logIcon: (status) => ({
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: status === 'completed' ? '#dcfce7' : status === 'failed' ? '#fee2e2' : '#fef3c7',
      color: status === 'completed' ? '#16a34a' : status === 'failed' ? '#dc2626' : '#d97706'
    }),
    logProvider: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0
    },
    logDate: {
      fontSize: '13px',
      color: '#6b7280',
      margin: '2px 0 0 0'
    },
    statusBadge: (status) => ({
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'capitalize',
      background: status === 'completed' ? '#dcfce7' : status === 'failed' ? '#fee2e2' : '#fef3c7',
      color: status === 'completed' ? '#166534' : status === 'failed' ? '#991b1b' : '#92400e'
    }),
    logStats: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
      paddingTop: '12px',
      borderTop: '1px solid #f3f4f6',
      marginTop: '12px'
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: '#4b5563'
    },
    statNumber: {
      fontWeight: '600',
      color: '#1f2937'
    },
    errorText: {
      fontSize: '13px',
      color: '#dc2626',
      margin: '12px 0 0 0',
      padding: '8px 12px',
      background: '#fef2f2',
      borderRadius: '6px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>Import Data</h3>
        <p style={styles.subtitle}>
          Import your existing data from other leave management platforms
        </p>
      </div>

      {/* Integration Cards */}
      <div style={styles.grid}>
        {/* VacationTracker.io Card */}
        <div
          style={styles.card}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#a78bfa';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.cardContent}>
            <div style={{ ...styles.iconBox, background: 'linear-gradient(135deg, #a78bfa, #7c3aed)' }}>
              <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div style={styles.cardBody}>
              <h4 style={styles.cardTitle}>VacationTracker.io</h4>
              <p style={styles.cardDesc}>
                Import users, departments, leave types, and approved leave history from your VacationTracker.io account
              </p>
              <button
                onClick={() => setShowVTModal(true)}
                style={styles.button}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Start Import
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Divider */}
      <div style={styles.divider} />

      {/* Import History */}
      <h4 style={styles.historyTitle}>Import History</h4>

      {loading ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>Loading...</p>
        </div>
      ) : logs.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <svg width="24" height="24" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p style={styles.emptyText}>No imports yet</p>
          <p style={styles.emptySubtext}>Your import history will appear here</p>
        </div>
      ) : (
        <div>
          {logs.map((log) => {
            const stats = parseStats(log.stats);
            const isSuccess = log.status === 'completed';
            const isFailed = log.status === 'failed';

            return (
              <div key={log.id} style={styles.logCard(log.status)}>
                <div style={styles.logHeader}>
                  <div style={styles.logInfo}>
                    <div style={styles.logIcon(log.status)}>
                      {isSuccess ? (
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      ) : isFailed ? (
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p style={styles.logProvider}>
                        {log.provider === 'vacationtracker' ? 'VacationTracker.io' : log.provider}
                      </p>
                      <p style={styles.logDate}>{formatDate(log.started_at)}</p>
                    </div>
                  </div>
                  <span style={styles.statusBadge(log.status)}>{log.status}</span>
                </div>

                {stats && isSuccess && (
                  <div style={styles.logStats}>
                    {stats.users && (stats.users.created > 0 || stats.users.mapped > 0) && (
                      <div style={styles.statItem}>
                        <svg width="14" height="14" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span style={styles.statNumber}>{stats.users.created + stats.users.mapped}</span> users
                      </div>
                    )}
                    {stats.departments && (stats.departments.created > 0 || stats.departments.mapped > 0) && (
                      <div style={styles.statItem}>
                        <svg width="14" height="14" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                        </svg>
                        <span style={styles.statNumber}>{stats.departments.created + stats.departments.mapped}</span> departments
                      </div>
                    )}
                    {stats.leaveTypes && (stats.leaveTypes.created > 0 || stats.leaveTypes.mapped > 0) && (
                      <div style={styles.statItem}>
                        <svg width="14" height="14" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span style={styles.statNumber}>{stats.leaveTypes.created + stats.leaveTypes.mapped}</span> leave types
                      </div>
                    )}
                    {stats.leaveRequests && stats.leaveRequests.imported > 0 && (
                      <div style={styles.statItem}>
                        <svg width="14" height="14" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span style={styles.statNumber}>{stats.leaveRequests.imported}</span> leave requests
                      </div>
                    )}
                  </div>
                )}

                {isFailed && log.error && (
                  <p style={styles.errorText}>Error: {log.error}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* VT Import Popup */}
      {showVTModal && (
        <VTImportPopup
          onClose={() => setShowVTModal(false)}
          onComplete={handleImportComplete}
        />
      )}
    </div>
  );
}
